import { computed, ref, watch } from "vue";
import type { DSTableColumn, DSTableProps, DSTableRow } from "@/src/design_system/data_display/table/types/props";

type SortDirection = "ascending" | "descending";

export const useDSTableSorting = (props: DSTableProps) => {
  const sortKey = ref<string | null>(null);
  const sortDirection = ref<SortDirection | null>(null);
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  const sortedColumn = computed(() =>
    props.columns.find(column => column.key === sortKey.value && column.sortable !== false)
  );

  watch(
    sortedColumn,
    column => {
      if (!column) {
        sortKey.value = null;
        sortDirection.value = null;
      }
    },
    { flush: "sync" }
  );

  const sortedRows = computed(() => {
    const column = sortedColumn.value;
    if (!column) return props.rows;
    const compareRows = (firstRow: DSTableRow, secondRow: DSTableRow) => {
      const first = firstRow[column.key];
      const second = secondRow[column.key];
      if (first == null) return second == null ? 0 : 1;
      if (second == null) return -1;
      const result = column.comparator
        ? column.comparator(first, second, firstRow, secondRow)
        : typeof first === "number" && typeof second === "number"
          ? first - second
          : collator.compare(String(first), String(second));
      return sortDirection.value === "ascending" ? result : -result;
    };
    const rows = [...props.rows];
    return column.sorter ? column.sorter(rows, compareRows) : rows.sort(compareRows);
  });

  const toggleSort = (column: DSTableColumn) => {
    if (column.sortable === false) return;
    if (sortKey.value !== column.key) {
      sortKey.value = column.key;
      sortDirection.value = "ascending";
    } else if (sortDirection.value === "ascending") {
      sortDirection.value = "descending";
    } else {
      sortKey.value = null;
      sortDirection.value = null;
    }
  };
  const ariaSort = (column: DSTableColumn) => {
    if (column.sortable === false || column.key !== sortedColumn.value?.key) return undefined;
    return sortDirection.value ?? undefined;
  };
  return { sortedRows, toggleSort, ariaSort };
};
